import { activityPageSchema, activityPeopleResponseSchema, type ActivityPage } from "@gioia365/contracts";
import { infiniteQueryOptions, queryOptions, type QueryClient } from "@tanstack/react-query";
import { apiRequest } from "../api/http";
import { commercialViewOf, tabKey, type TabContext, type TabKey } from "../session/tab-context";
import type { ActivityRequest } from "./activity-search";

// Le letture di «Attività» (SEZ-8, M7 parte 2): `GET /api/activity` a pagine con il cursore, con periodo, «Di chi», «Che cosa» e la
// ricerca dell'indirizzo, e le persone di «Di chi». La prima pagina di partenza la legge in anticipo la voce della rail, come le altre.

/** Le righe di una pagina (il server ne accetta da 1 a 100, di partenza 50). */
export const ACTIVITY_PAGE_SIZE = 50;

/** La vista di partenza dell'indirizzo `/attivita` senza parametri: «Oggi», tutte le azioni che si vedono. */
export const DEFAULT_ACTIVITY_QUERY: ActivityRequest = { period: "today" };

export const activityKeys = {
  all: (tab: TabKey) => ["scheda", tab, "activity"] as const,
  pages: (tab: TabKey, view: string | null, query: unknown) => ["scheda", tab, "activity", "pages", view, query] as const,
  people: (tab: TabKey, view: string | null) => ["scheda", tab, "activity", "people", view] as const,
};

function activityPath(query: ActivityRequest, cursor: string | null): string {
  const params = new URLSearchParams({ period: query.period });
  if (query.from !== undefined) params.set("from", query.from);
  if (query.to !== undefined) params.set("to", query.to);
  if (query.who !== undefined) params.set("who", query.who);
  if (query.group !== undefined) params.set("group", query.group);
  if (query.q !== undefined && query.q !== "") params.set("q", query.q);
  if (cursor !== null) params.set("cursor", cursor);
  params.set("limit", String(ACTIVITY_PAGE_SIZE));
  return `/api/activity?${params.toString()}`;
}

/** Le pagine delle azioni: la successiva dal cursore della precedente, finché il server ne dà uno. */
export function activityPagesQuery(tab: TabContext, query: ActivityRequest, view: string | null) {
  return infiniteQueryOptions({
    queryKey: activityKeys.pages(tabKey(tab), view, query),
    queryFn: ({ signal, pageParam }) => apiRequest("GET", activityPath(query, pageParam), { schema: activityPageSchema, tab, signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (last: ActivityPage) => last.nextCursor ?? undefined,
    // Tornare alla pagina entro mezzo minuto la mostra subito; dopo si rilegge, perché il registro cresce di continuo.
    staleTime: 30_000,
  });
}

/** Le persone di «Di chi», tutte quelle dell'azienda (anche sospese o archiviate); vuota per chi vede soltanto le sue azioni. */
export function activityPeopleQuery(tab: TabContext, view: string | null) {
  return queryOptions({
    queryKey: activityKeys.people(tabKey(tab), view),
    queryFn: async ({ signal }) => (await apiRequest("GET", "/api/activity/people", { schema: activityPeopleResponseSchema, tab, signal })).people,
    staleTime: 60_000,
  });
}

/** Legge in anticipo la prima pagina di partenza, per la voce della rail, nella «Guarda come…» della scheda se c'è. */
export function prefetchActivity(queryClient: QueryClient, tab: TabContext): Promise<void> {
  return queryClient.prefetchInfiniteQuery(activityPagesQuery(tab, DEFAULT_ACTIVITY_QUERY, commercialViewOf(tab)));
}
